import React, { useEffect, useState, useMemo } from 'react';
import useQuickPredictionsALL from '../hooks/useQuickPredictionsALL';
import './PickstarPredictionsViewer.css';

const PickstarPredictionsViewer = () => {
  const [mounted, setMounted] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [expandedGame, setExpandedGame] = useState(null);
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState(new Set());
  const [expandedLeagues, setExpandedLeagues] = useState(new Set());
  const [loadingGames, setLoadingGames] = useState(false);

  const {
    loading,
    predictions,
    error,
    progress,
    runPredictionsForSelectedGames,
    getTodaysPredictions,
    getTodaysGames,
    stopPredictions
  } = useQuickPredictionsALL();

  // Format date to ET timezone
  const formatDateKey = (date) => {
    const nycDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const year = nycDate.getFullYear();
    const month = String(nycDate.getMonth() + 1).padStart(2, '0');
    const day = String(nycDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize with today's date
  useEffect(() => {
    console.log('%c🚀 PICKSTAR PREDICTIONS VIEWER WITH SELECTION MOUNTED', 'background: blue; color: white; font-size: 16px; padding: 8px;');
    const today = formatDateKey(new Date());
    setSelectedDate(today);
    setMounted(true);
  }, []);

  // Load available games and predictions when date changes
  useEffect(() => {
    if (mounted && selectedDate) {
      loadAvailableGames();
      getTodaysPredictions(selectedDate);
    }
  }, [selectedDate, mounted, getTodaysPredictions]);

  // Load games from settlement service
  const loadAvailableGames = async () => {
    setLoadingGames(true);
    try {
      const games = await getTodaysGames(selectedDate);

      // Filter for games we want to predict (not finished games)
      const eligibleGames = games.filter(game => {
        const status = game.status?.short;
        return !status || ['NS', '1H', 'HT', '2H', 'SUSP', 'INT'].includes(status);
      });

      setAvailableGames(eligibleGames);
      console.log(`Loaded ${eligibleGames.length} eligible games out of ${games.length} total`);
    } catch (err) {
      console.error('Error loading games:', err);
    } finally {
      setLoadingGames(false);
    }
  };

  // Group games by league
  const gamesByLeague = useMemo(() => {
    const grouped = {};
    availableGames.forEach(game => {
      const leagueKey = `${game.league.id}_${game.league.name}_${game.league.country}`;
      if (!grouped[leagueKey]) {
        grouped[leagueKey] = {
          league: game.league,
          games: []
        };
      }
      grouped[leagueKey].games.push(game);
    });

    // Sort leagues by number of games (descending)
    return Object.entries(grouped)
      .sort((a, b) => b[1].games.length - a[1].games.length)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }, [availableGames]);

  // Toggle game selection
  const toggleGameSelection = (gameId) => {
    const newSelection = new Set(selectedGames);
    if (newSelection.has(gameId)) {
      newSelection.delete(gameId);
    } else {
      newSelection.add(gameId);
    }
    setSelectedGames(newSelection);
  };

  // Toggle all games in a league
  const toggleLeagueSelection = (leagueKey) => {
    const newSelection = new Set(selectedGames);
    const leagueGames = gamesByLeague[leagueKey].games;
    const allSelected = leagueGames.every(game => selectedGames.has(game.gameId));

    leagueGames.forEach(game => {
      if (allSelected) {
        newSelection.delete(game.gameId);
      } else {
        newSelection.add(game.gameId);
      }
    });

    setSelectedGames(newSelection);
  };

  // Select all games
  const selectAllGames = () => {
    const allGameIds = new Set(availableGames.map(game => game.gameId));
    setSelectedGames(allGameIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedGames(new Set());
  };

  // Toggle league expansion
  const toggleLeagueExpansion = (leagueKey) => {
    const newExpanded = new Set(expandedLeagues);
    if (newExpanded.has(leagueKey)) {
      newExpanded.delete(leagueKey);
    } else {
      newExpanded.add(leagueKey);
    }
    setExpandedLeagues(newExpanded);
  };

  // Run predictions for selected games
  const handleRunPredictions = async () => {
    if (selectedGames.size === 0) {
      alert('Please select at least one game to predict');
      return;
    }

    const gamesToPredict = availableGames.filter(game => selectedGames.has(game.gameId));
    await runPredictionsForSelectedGames(gamesToPredict, selectedDate);
  };

  // Calculate statistics
  const stats = {
    total: predictions.length,
    successful: predictions.filter(p => !p.prediction.error).length,
    failed: predictions.filter(p => p.prediction.error).length,
    highConfidence: predictions.filter(p => p.prediction.confidence >= 70).length,
    medConfidence: predictions.filter(p => p.prediction.confidence >= 40 && p.prediction.confidence < 70).length,
    lowConfidence: predictions.filter(p => p.prediction.confidence < 40).length
  };

  return (
    <div className="settlement-viewer">
      <div className="viewer-header">
        <h1>PickStar AI Predictions - Game Selection</h1>
        <div className="header-controls">
          <div className="date-selector">
            <label>Date (ET): </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <button
            className="refresh-btn"
            onClick={loadAvailableGames}
            disabled={loadingGames}
          >
            {loadingGames ? 'Loading...' : 'Refresh Games'}
          </button>
          {!loading ? (
            <button
              className="run-predictions-btn"
              onClick={handleRunPredictions}
              disabled={selectedGames.size === 0}
            >
              Run Predictions ({selectedGames.size} games)
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="run-predictions-btn loading"
                disabled
              >
                Processing {progress.current}/{progress.total}
              </button>
              <button
                className="stop-predictions-btn"
                onClick={stopPredictions}
              >
                STOP
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {/* Game Selection Panel */}
      <div className="game-selection-panel" style={{
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid rgba(255,255,255,0.1)',
        color: '#fff'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2 style={{ color: '#fff' }}>Available Games ({availableGames.length})</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={selectAllGames}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Select All
            </button>
            <button
              onClick={clearSelection}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>

        {loadingGames ? (
          <div style={{ color: '#fff' }}>Loading available games...</div>
        ) : availableGames.length === 0 ? (
          <div style={{ color: '#fff' }}>No eligible games found. Run the settlement service to load games.</div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {Object.entries(gamesByLeague).map(([leagueKey, leagueData]) => {
              const isExpanded = expandedLeagues.has(leagueKey);
              const leagueGames = leagueData.games;
              const selectedInLeague = leagueGames.filter(g => selectedGames.has(g.gameId)).length;
              const allInLeagueSelected = selectedInLeague === leagueGames.length && leagueGames.length > 0;

              return (
                <div key={leagueKey} style={{
                  marginBottom: '10px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {/* League Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    background: 'rgba(255,255,255,0.02)',
                    cursor: 'pointer'
                  }} onClick={() => toggleLeagueExpansion(leagueKey)}>
                    <input
                      type="checkbox"
                      checked={allInLeagueSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleLeagueSelection(leagueKey);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginRight: '10px' }}
                    />
                    <span style={{ flex: 1, color: '#fff' }}>
                      <strong>{leagueData.league.name}</strong> - {leagueData.league.country}
                    </span>
                    <span style={{ marginRight: '10px', color: 'rgba(255,255,255,0.7)' }}>
                      {selectedInLeague}/{leagueGames.length} selected
                    </span>
                    <span>{isExpanded ? '▼' : '▶'}</span>
                  </div>

                  {/* League Games */}
                  {isExpanded && (
                    <div style={{ padding: '10px', paddingTop: '5px' }}>
                      {leagueGames.map(game => {
                        const gameTime = new Date(game.timestamp);
                        const formattedTime = gameTime.toLocaleString("en-US", {
                          timeZone: "America/New_York",
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        });

                        return (
                          <div key={game.gameId} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '5px',
                            borderRadius: '4px',
                            marginBottom: '5px',
                            background: selectedGames.has(game.gameId) ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                            border: `1px solid ${selectedGames.has(game.gameId) ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.05)'}`
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedGames.has(game.gameId)}
                              onChange={() => toggleGameSelection(game.gameId)}
                              style={{ marginRight: '10px' }}
                            />
                            <span style={{ flex: 1, fontSize: '13px' }}>
                              <span style={{ color: '#10b981' }}>{game.teams.home.name}</span>
                              <span style={{ color: 'rgba(255,255,255,0.3)', margin: '0 5px' }}>vs</span>
                              <span style={{ color: '#ef4444' }}>{game.teams.away.name}</span>
                            </span>
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                              {formattedTime}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Statistics Panel */}
      <div className="stats-panel">
        <h2>Prediction Statistics</h2>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Total Games</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Successful</span>
            <span className="stat-value success">{stats.successful}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Failed</span>
            <span className="stat-value error">{stats.failed}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">High Confidence (≥70%)</span>
            <span className="stat-value">{stats.highConfidence}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Med Confidence (40-69%)</span>
            <span className="stat-value">{stats.medConfidence}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Low Confidence (&lt;40%)</span>
            <span className="stat-value">{stats.lowConfidence}</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {loading && progress.total > 0 && (
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            />
          </div>
          <span className="progress-text">
            Processing: {progress.current} / {progress.total} games
          </span>
        </div>
      )}

      {/* Predictions List */}
      <div className="games-container">
        <h2>Game Predictions</h2>
        {predictions.length === 0 ? (
          <div className="no-data">
            No predictions found for {selectedDate}
          </div>
        ) : (
          <div className="games-list">
            {predictions.map((pred) => {
              const gameTimestamp = pred.timestamp || (pred.fixture?.timestamp * 1000);
              const gameTime = new Date(gameTimestamp);
              const formattedTime = gameTime.toLocaleString("en-US", {
                timeZone: "America/New_York",
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              });

              const hasError = !!pred.prediction.error;

              return (
                <div
                  key={pred.gameId}
                  className="game-card"
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderLeft: `3px solid ${
                      hasError ? '#ef4444' :
                      pred.prediction.confidence >= 70 ? '#10b981' :
                      pred.prediction.confidence >= 40 ? '#fbbf24' : '#ef4444'
                    }`,
                    background: hasError ? 'rgba(239, 68, 68, 0.03)' :
                               pred.prediction.confidence >= 70 ? 'rgba(16, 185, 129, 0.03)' :
                               pred.prediction.confidence >= 40 ? 'rgba(251, 191, 36, 0.03)' :
                               'rgba(239, 68, 68, 0.03)',
                    minHeight: '40px',
                    cursor: 'default'
                  }}
                >
                  {/* Teams and League */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    {/* Teams */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      minWidth: '250px'
                    }}>
                      <span style={{ color: '#10b981' }}>{pred.teams.home.name}</span>
                      <span style={{ color: 'rgba(255,255,255,0.3)' }}>vs</span>
                      <span style={{ color: '#ef4444' }}>{pred.teams.away.name}</span>
                    </div>

                    {/* League */}
                    <div style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      minWidth: '80px'
                    }}>
                      {pred.league.name}
                    </div>

                    {/* Pick */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      flex: 1
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Pick:</span>
                      <strong style={{ color: '#fff' }}>{pred.prediction.winner || 'N/A'}</strong>
                    </div>

                    {/* Reason - truncated */}
                    {pred.prediction.reason && (
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.5)',
                        fontStyle: 'italic',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {pred.prediction.reason}
                      </div>
                    )}
                  </div>

                  {/* Right side: Time and Confidence */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {/* Time */}
                    <span style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.4)',
                      minWidth: '60px',
                      textAlign: 'right'
                    }}>
                      {formattedTime}
                    </span>

                    {/* Confidence */}
                    {!hasError && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: pred.prediction.confidence >= 70 ? 'rgba(16, 185, 129, 0.2)' :
                                   pred.prediction.confidence >= 40 ? 'rgba(251, 191, 36, 0.2)' :
                                   'rgba(239, 68, 68, 0.2)',
                        color: pred.prediction.confidence >= 70 ? '#10b981' :
                               pred.prediction.confidence >= 40 ? '#fbbf24' : '#ef4444',
                        minWidth: '45px',
                        textAlign: 'center'
                      }}>
                        {pred.prediction.confidence?.toFixed(1)}%
                      </span>
                    )}

                    {/* Confidence Level */}
                    {!hasError && (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '700',
                        background: pred.prediction.level === 'HIGH' ? 'rgba(16, 185, 129, 0.2)' :
                                   pred.prediction.level === 'MED' ? 'rgba(251, 191, 36, 0.2)' :
                                   'rgba(239, 68, 68, 0.2)',
                        color: pred.prediction.level === 'HIGH' ? '#10b981' :
                               pred.prediction.level === 'MED' ? '#fbbf24' : '#ef4444',
                        minWidth: '40px',
                        textAlign: 'center'
                      }}>
                        {pred.prediction.level || 'LOW'}
                      </span>
                    )}

                    {/* Error badge */}
                    {hasError && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '700',
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444'
                      }}>
                        FAILED
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PickstarPredictionsViewer;

// import React, { useEffect, useState } from 'react';
// import useQuickPredictionsALL from '../hooks/useQuickPredictionsALL';
// import './PickstarPredictionsViewer.css';

// const PickstarPredictionsViewer = () => {
//   const [mounted, setMounted] = useState(false);
//   const [selectedDate, setSelectedDate] = useState('');
//   const [expandedGame, setExpandedGame] = useState(null);

//   const {
//     loading,
//     predictions,
//     error,
//     progress,
//     runPredictionsForAllGames,
//     getTodaysPredictions,
//     stopPredictions
//   } = useQuickPredictionsALL();

//   // Format date to ET timezone
//   const formatDateKey = (date) => {
//     const nycDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
//     const year = nycDate.getFullYear();
//     const month = String(nycDate.getMonth() + 1).padStart(2, '0');
//     const day = String(nycDate.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
//   };

//   // Initialize with today's date
//   useEffect(() => {
//     console.log('%c🚀 PICKSTAR PREDICTIONS VIEWER MOUNTED', 'background: blue; color: white; font-size: 16px; padding: 8px;');
//     const today = formatDateKey(new Date());
//     setSelectedDate(today);
//     setMounted(true);
//   }, []);

//   // Load predictions when date changes
//   useEffect(() => {
//     if (mounted && selectedDate) {
//       getTodaysPredictions(selectedDate);
//     }
//   }, [selectedDate, mounted, getTodaysPredictions]);

//   // No need for admin check here - it's handled in the route

//   const toggleGameExpansion = (gameId) => {
//     setExpandedGame(expandedGame === gameId ? null : gameId);
//   };

//   // Calculate statistics
//   const stats = {
//     total: predictions.length,
//     successful: predictions.filter(p => !p.prediction.error).length,
//     failed: predictions.filter(p => p.prediction.error).length,
//     highConfidence: predictions.filter(p => p.prediction.confidence >= 70).length,
//     medConfidence: predictions.filter(p => p.prediction.confidence >= 40 && p.prediction.confidence < 70).length,
//     lowConfidence: predictions.filter(p => p.prediction.confidence < 40).length
//   };

//   return (
//     <div className="settlement-viewer">
//       <div className="viewer-header">
//         <h1>PickStar AI Predictions</h1>
//         <div className="header-controls">
//           <div className="date-selector">
//             <label>Date (ET): </label>
//             <input
//               type="date"
//               value={selectedDate}
//               onChange={(e) => setSelectedDate(e.target.value)}
//               max={formatDateKey(new Date())}
//             />
//           </div>
//           {!loading ? (
//             <button
//               className="run-predictions-btn"
//               onClick={runPredictionsForAllGames}
//             >
//               Run Predictions for All Games
//             </button>
//           ) : (
//             <div style={{ display: 'flex', gap: '10px' }}>
//               <button
//                 className="run-predictions-btn loading"
//                 disabled
//               >
//                 Processing {progress.current}/{progress.total}
//               </button>
//               <button
//                 className="stop-predictions-btn"
//                 onClick={stopPredictions}
//               >
//                 STOP
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {error && (
//         <div className="error-message">
//           Error: {error}
//         </div>
//       )}

//       {/* Statistics Panel */}
//       <div className="stats-panel">
//         <h2>Prediction Statistics</h2>
//         <div className="stats-grid">
//           <div className="stat-item">
//             <span className="stat-label">Total Games</span>
//             <span className="stat-value">{stats.total}</span>
//           </div>
//           <div className="stat-item">
//             <span className="stat-label">Successful</span>
//             <span className="stat-value success">{stats.successful}</span>
//           </div>
//           <div className="stat-item">
//             <span className="stat-label">Failed</span>
//             <span className="stat-value error">{stats.failed}</span>
//           </div>
//           <div className="stat-item">
//             <span className="stat-label">High Confidence (≥70%)</span>
//             <span className="stat-value">{stats.highConfidence}</span>
//           </div>
//           <div className="stat-item">
//             <span className="stat-label">Med Confidence (40-69%)</span>
//             <span className="stat-value">{stats.medConfidence}</span>
//           </div>
//           <div className="stat-item">
//             <span className="stat-label">Low Confidence (&lt;40%)</span>
//             <span className="stat-value">{stats.lowConfidence}</span>
//           </div>
//         </div>
//       </div>

//       {/* Progress Bar */}
//       {loading && progress.total > 0 && (
//         <div className="progress-container">
//           <div className="progress-bar">
//             <div
//               className="progress-fill"
//               style={{ width: `${(progress.current / progress.total) * 100}%` }}
//             />
//           </div>
//           <span className="progress-text">
//             Processing: {progress.current} / {progress.total} games
//           </span>
//         </div>
//       )}

//       {/* Predictions List */}
//       <div className="games-container">
//         <h2>Game Predictions</h2>
//         {predictions.length === 0 ? (
//           <div className="no-data">
//             No predictions found for {selectedDate}
//           </div>
//         ) : (
//           <div className="games-list">
//             {predictions.map((pred) => {
//               const gameTimestamp = pred.timestamp || (pred.fixture?.timestamp * 1000);
//               const gameTime = new Date(gameTimestamp);
//               const formattedTime = gameTime.toLocaleString("en-US", {
//                 timeZone: "America/New_York",
//                 hour: 'numeric',
//                 minute: '2-digit',
//                 hour12: true
//               });

//               const hasError = !!pred.prediction.error;

//               return (
//                 <div
//                   key={pred.gameId}
//                   className="game-card"
//                   style={{
//                     padding: '8px 12px',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'space-between',
//                     borderLeft: `3px solid ${
//                       hasError ? '#ef4444' :
//                       pred.prediction.confidence >= 70 ? '#10b981' :
//                       pred.prediction.confidence >= 40 ? '#fbbf24' : '#ef4444'
//                     }`,
//                     background: hasError ? 'rgba(239, 68, 68, 0.03)' :
//                                pred.prediction.confidence >= 70 ? 'rgba(16, 185, 129, 0.03)' :
//                                pred.prediction.confidence >= 40 ? 'rgba(251, 191, 36, 0.03)' :
//                                'rgba(239, 68, 68, 0.03)',
//                     minHeight: '40px',
//                     cursor: 'default'
//                   }}
//                 >
//                   {/* Teams and League */}
//                   <div style={{
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '15px',
//                     flex: 1,
//                     minWidth: 0
//                   }}>
//                     {/* Teams */}
//                     <div style={{
//                       display: 'flex',
//                       alignItems: 'center',
//                       gap: '8px',
//                       fontSize: '13px',
//                       minWidth: '250px'
//                     }}>
//                       <span style={{ color: '#10b981' }}>{pred.teams.home.name}</span>
//                       <span style={{ color: 'rgba(255,255,255,0.3)' }}>vs</span>
//                       <span style={{ color: '#ef4444' }}>{pred.teams.away.name}</span>
//                     </div>

//                     {/* League */}
//                     <div style={{
//                       fontSize: '11px',
//                       color: 'rgba(255,255,255,0.4)',
//                       textTransform: 'uppercase',
//                       letterSpacing: '0.05em',
//                       minWidth: '80px'
//                     }}>
//                       {pred.league.name}
//                     </div>

//                     {/* Pick */}
//                     <div style={{
//                       display: 'flex',
//                       alignItems: 'center',
//                       gap: '8px',
//                       fontSize: '13px',
//                       flex: 1
//                     }}>
//                       <span style={{ color: 'rgba(255,255,255,0.5)' }}>Pick:</span>
//                       <strong style={{ color: '#fff' }}>{pred.prediction.winner || 'N/A'}</strong>
//                     </div>

//                     {/* Reason - truncated */}
//                     {pred.prediction.reason && (
//                       <div style={{
//                         fontSize: '12px',
//                         color: 'rgba(255,255,255,0.5)',
//                         fontStyle: 'italic',
//                         maxWidth: '200px',
//                         overflow: 'hidden',
//                         textOverflow: 'ellipsis',
//                         whiteSpace: 'nowrap'
//                       }}>
//                         {pred.prediction.reason}
//                       </div>
//                     )}
//                   </div>

//                   {/* Right side: Time and Confidence */}
//                   <div style={{
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '12px'
//                   }}>
//                     {/* Time */}
//                     <span style={{
//                       fontSize: '11px',
//                       color: 'rgba(255,255,255,0.4)',
//                       minWidth: '60px',
//                       textAlign: 'right'
//                     }}>
//                       {formattedTime}
//                     </span>

//                     {/* Confidence */}
//                     {!hasError && (
//                       <span style={{
//                         padding: '2px 8px',
//                         borderRadius: '12px',
//                         fontSize: '11px',
//                         fontWeight: '600',
//                         background: pred.prediction.confidence >= 70 ? 'rgba(16, 185, 129, 0.2)' :
//                                    pred.prediction.confidence >= 40 ? 'rgba(251, 191, 36, 0.2)' :
//                                    'rgba(239, 68, 68, 0.2)',
//                         color: pred.prediction.confidence >= 70 ? '#10b981' :
//                                pred.prediction.confidence >= 40 ? '#fbbf24' : '#ef4444',
//                         minWidth: '45px',
//                         textAlign: 'center'
//                       }}>
//                         {pred.prediction.confidence?.toFixed(1)}%
//                       </span>
//                     )}

//                     {/* Confidence Level */}
//                     {!hasError && (
//                       <span style={{
//                         padding: '2px 6px',
//                         borderRadius: '4px',
//                         fontSize: '10px',
//                         fontWeight: '700',
//                         background: pred.prediction.level === 'HIGH' ? 'rgba(16, 185, 129, 0.2)' :
//                                    pred.prediction.level === 'MED' ? 'rgba(251, 191, 36, 0.2)' :
//                                    'rgba(239, 68, 68, 0.2)',
//                         color: pred.prediction.level === 'HIGH' ? '#10b981' :
//                                pred.prediction.level === 'MED' ? '#fbbf24' : '#ef4444',
//                         minWidth: '40px',
//                         textAlign: 'center'
//                       }}>
//                         {pred.prediction.level || 'LOW'}
//                       </span>
//                     )}

//                     {/* Error badge */}
//                     {hasError && (
//                       <span style={{
//                         padding: '2px 8px',
//                         borderRadius: '4px',
//                         fontSize: '11px',
//                         fontWeight: '700',
//                         background: 'rgba(239, 68, 68, 0.2)',
//                         color: '#ef4444'
//                       }}>
//                         FAILED
//                       </span>
//                     )}
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default PickstarPredictionsViewer;